import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Loader} from '@components/common/base/loader'
import {Button} from '@components/common/button/button'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {ChatListNew} from '@components/right-panel/chat-list/chat-list-new'
import {useBoard, useGlobalKeyDown} from '@containers/hooks'
import {idTasksStore} from '@extensions/id-tasks/id-tasks-store'
import {Search, searchObserver} from '@extensions/search'
import {
  SearchHintNoQueryCustom,
  SearchHintNoQueryPredefined
} from './search-hint'
import {SearchNotFound} from './search-not-found'
import {SearchResultView} from './search-results'
import {itemHeight, SearchStore, useSearchStore} from './search-store'

const useListKeyboardHandlers = (store: SearchStore): void => {
  useGlobalKeyDown({
    anyKey(opts) {
      if (opts.key === 'Home' || (opts.key === 'ArrowUp' && opts.ctrl)) {
        return store.listHome()
      } else if (
        opts.key === 'End' ||
        (opts.key === 'ArrowDown' && opts.ctrl)
      ) {
        return store.listEnd()
      } else if (
        opts.key === 'PageUp' ||
        (opts.key === 'ArrowUp' && opts.alt)
      ) {
        return store.listPageUp()
      } else if (
        opts.key === 'PageDown' ||
        (opts.key === 'ArrowDown' && opts.alt)
      ) {
        return store.listPageDown()
      } else if (opts.key === 'ArrowUp') {
        return store.listUp()
      } else if (opts.key === 'ArrowDown') {
        return store.listDown()
      } else if (opts.key === 'Enter') {
        return store.listEnter()
      }
      return false
    }
  })
}

const InProgress = () => (
  <div className="pointer-events-none absolute z-10 top-0 bottom-0 left-0 right-0 flex items-center justify-center">
    <div className="absolute -top-8 -bottom-8 -left-8 -right-8 bg-background-primary opacity-40" />
    <Loader size="small" />
  </div>
)

export interface SearchResultsNewProps {
  hideTitle?: boolean
  refreshChangedProject: boolean
}

export const SearchResultsNew = observer((props: SearchResultsNewProps) => {
  const {hideTitle, refreshChangedProject} = props
  const [state, store] = useSearchStore()

  useListKeyboardHandlers(store)
  useEffect(() => {
    // `refresh` is a heavy function, so refresh only if an id is searched
    if (Search.isIdQuery(store.state.debouncedValue)) {
      store.refreshSearch({quiet: true})
    }
  }, [store, idTasksStore.state.modified])

  const board = useBoard()
  useEffect(() => {
    if (refreshChangedProject) {
      store.refreshSearch({quiet: true})
    }
  }, [board?.location, refreshChangedProject])

  useEffect(() => {
    store.refreshSearch({quiet: true})
  }, [searchObserver.state.refreshSearch])

  if (!state.debouncedValue) {
    return state.filterState.type === 'custom' ? (
      <SearchHintNoQueryCustom />
    ) : (
      <SearchHintNoQueryPredefined />
    )
  } else if (!state.inProgress && !store.results.length) {
    return <SearchNotFound />
  } else {
    return (
      <div className="flex-1 flex min-h-0 flex-col gap-8 relative isolate">
        {state.inProgress && <InProgress />}
        {!hideTitle && !!store.results.length && (
          <div className="p-8 text-sm-regular">{Lang.msg.searchResults}</div>
        )}
        <WithPerfectScrollbar
          containerRef={store.setScrollableEl}
          onScroll={store.onListScroll}
          onMouseMove={store.showCursor}
          className="flex-1 pr-8 -mr-8"
          wheelPropagation
          wheelSpeed={1.5}
        >
          <ChatListNew
            ref={store.setContainerEl}
            chatView={SearchResultView}
            chats={store.results}
            selectedIx={state.selectedIx}
            from={state.listFrom}
            to={state.listTo}
            itemClick={store.itemClick}
            itemHover={store.itemHover}
            itemHeight={itemHeight}
          />

          {state.totalCountRaw > state.resultsRaw.length && (
            <div className="flex flex-col items-center gap-8">
              <span className="text-sm text-secondary">
                {Lang.msg.searchResultsLimited(state.totalCountRaw)}
              </span>
              <Button onClick={store.setNoLimit}>
                {Lang.msg.searchResultsShowAll}
              </Button>
            </div>
          )}
        </WithPerfectScrollbar>
      </div>
    )
  }
})
